import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { Spin, Empty } from "antd";
import { ReportPage } from "../../../components";
import { GetSignOffReport } from "../../../Api";

export const SignOffDetailReport: React.FC = () => {
  const [searchParams] = useSearchParams();
  const signOffId = searchParams.get("id") || "";

  const {
    data: responseData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["sign-off-detail", signOffId],
    queryFn: () => GetSignOffReport({ id: signOffId }),
    enabled: !!signOffId,
  });

  const detailData = useMemo(() => responseData?.data || null, [responseData]);

  return (
    <ReportPage
      title="Sign Off Report Detail"
      description="Detailed cash shift summary, physical denominations counts, and reconciliation."
      icon={<FileText className="w-6 h-6 text-indigo-650" />}
      backPath="/system/reports/sign-off-list"
      isCustomRender={true}
      onRefresh={refetch}
    >
      {isLoading ? (
        <div className="p-20 text-center flex flex-col items-center justify-center gap-3 bg-white rounded-2xl border border-gray-200/80 shadow-sm">
          <Spin size="large" />
          <span className="text-xs text-gray-400 font-medium">
            Fetching sign off details...
          </span>
        </div>
      ) : !detailData ? (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden flex-1 p-20 text-center">
          <Empty
            description={
              <span className="text-xs font-semibold text-gray-400">
                No Sign Off report data found
              </span>
            }
          />
        </div>
      ) : (
        <div className="flex justify-center receipt-container py-4">
          <div className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-gray-200 p-6 font-mono text-xs text-gray-800 flex flex-col gap-4">
            <div className="text-center flex flex-col gap-1">
              <span className="text-base font-black tracking-wider uppercase">
                Sign Off Report
              </span>
              <span className="text-[10px] text-gray-500 uppercase">
                {detailData.store_name}
              </span>
            </div>

            <div className="border-t border-dashed border-gray-300 pt-3 flex flex-col gap-1">
              <div className="flex justify-between">
                <span>Date:</span>
                <span className="font-bold">{detailData.date}</span>
              </div>
              <div className="flex justify-between">
                <span>Time:</span>
                <span className="font-bold">{detailData.time}</span>
              </div>
              <div className="flex justify-between">
                <span>Sign Off User:</span>
                <span className="font-bold">{detailData.sign_off_user}</span>
              </div>
            </div>

            <div className="border-t border-dashed border-gray-300 pt-3">
              <div className="font-bold uppercase tracking-wider mb-2 text-center border-b pb-1">
                Cash Denomination
              </div>
              <div className="flex flex-col gap-1">
                {(detailData.denominations || []).map((item: any) => (
                  <div key={item.denom} className="flex justify-between">
                    <span>
                      {item.denom.toString().padEnd(4, " ")} x{" "}
                      {item.count.toString().padStart(2, "0")}
                    </span>
                    <span>{Number(item.total).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-dashed border-gray-300 mt-2 pt-2 flex justify-between font-bold text-sm">
                <span>Total:</span>
                <span>{Number(detailData.physical_balance).toFixed(2)}</span>
              </div>
            </div>

            <div className="border-t border-dashed border-gray-300 pt-3 flex flex-col gap-1.5">
              <div className="flex justify-between">
                <span>Card Payments</span>
                <span className="font-bold">
                  {Number(detailData.card_payments).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Cheque Payments</span>
                <span className="font-bold">
                  {Number(detailData.cheque_payments).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Bank Deposits</span>
                <span className="font-bold">
                  {Number(detailData.bank_deposits).toFixed(2)}
                </span>
              </div>
              {(detailData.other_payments || []).map((item: any) => (
                <div key={item.code} className="flex justify-between">
                  <span>{item.name}</span>
                  <span className="font-bold">
                    {Number(item.total).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            <div className="border-t border-dashed border-gray-300 pt-3 flex flex-col gap-1.5">
              <div className="flex justify-between">
                <span>Opening Balance</span>
                <span className="font-bold">
                  {Number(detailData.opening_balance).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>+ Cash Payments</span>
                <span className="font-bold text-emerald-600">
                  +{Number(detailData.cash_payments).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>- Withdrawals</span>
                <span className="font-bold text-rose-500">
                  -{Number(detailData.withdrawals).toFixed(2)}
                </span>
              </div>
            </div>

            <div className="border-t border-dashed border-gray-300 pt-3 flex flex-col gap-1.5">
              <div className="font-bold uppercase tracking-wider mb-1 text-center">
                Cash Balance
              </div>
              <div className="flex justify-between">
                <span>Calculated:</span>
                <span className="font-bold">
                  {Number(detailData.calculated_balance).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Physical:</span>
                <span className="font-bold">
                  {Number(detailData.physical_balance).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between border-t border-double border-gray-400 pt-1.5 font-extrabold text-sm">
                <span>Sort Amount:</span>
                <span
                  className={
                    detailData.short_over_amount < 0
                      ? "text-rose-600"
                      : detailData.short_over_amount > 0
                        ? "text-emerald-600"
                        : ""
                  }
                >
                  {Number(detailData.short_over_amount).toFixed(2)}
                </span>
              </div>
            </div>

            {detailData.note && (
              <div className="border-t border-dashed border-gray-300 pt-3">
                <div className="font-bold uppercase tracking-wider mb-1">
                  Note:
                </div>
                <div className="text-[11px] text-gray-600 whitespace-pre-wrap">
                  {detailData.note}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </ReportPage>
  );
};
